import React, { useMemo } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Card,
  Icon,
  toGlyphName,
  type GlyphName,
  type IconName,
  type Color,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  ScrollArea,
  Skeleton,
  SkeletonGroup,
  Switch,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  Text,
  Textarea,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";

import { copyText } from "#app/lib/utils/copy-actions";
import { ListRowsSkeleton } from "#app/components/loading/list-rows-skeleton";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useAutomations } from "#app/hooks/automations/use-automations";
import { useAutomationsDialogActions } from "#app/lib/automations-dialog-context";
import { automationLiveStatus } from "#app/components/automations/automation-status";
import { inboundConnectionIdOf } from "#app/hooks/automations/use-automation-inbound-sources";
import { outboundConnectionIdOf } from "#app/hooks/automations/use-automation-outbound-targets";
import {
  ConfigSection,
  IconChip,
  OptionRow,
} from "./integrations-config-primitives";
import {
  type ConnectionGuide,
  type RemoteCollection,
  type IntegrationConnection,
  type IntegrationKind,
  type IntegrationProvider,
} from "./integrations-data";
import {
  useConnectionSettings,
  useConnectionStatus,
  useNewConnectionForm,
  useRemoteCollection,
  useStartIntegrationOAuth,
  useRotateWebhook,
} from "./use-connection-config";
import { DeleteConnectionConfirm } from "./delete-connection-dialog";
import {
  type DetailTab,
  fieldGroupError,
  newConnectionNextStep,
  newSaveButtonTooltip,
  newTestButtonTooltip,
  saveButtonTooltip,
  settingsNextStep,
  splitEntries,
  tabsForProvider,
  testButtonTooltip,
} from "./connection-config-utils";

// Sentinel for "configuring a brand-new connection" (no persisted row yet).
// Exported so the hosting dialog can label its breadcrumb's page segment.
export const NEW_CONNECTION = "__new__";

// The `setup` tab is the "Configuration Wizard" (provider-declared setup fields,
// else a placeholder); the `settings` tab is "Connection Setup" (edits
// credentials/settings); Webhook surfaces the inbound POST URL for every CRM and
// ingestion connection. Tab set → `tabsForProvider`; keys stay `setup`/`settings`
// (labels differ).
const DETAIL_TAB_ICONS: Record<DetailTab, IconName> = {
  setup: "wand-sparkles",
  automations: "zap",
  features: "list",
  webhook: "webhook",
  settings: "settings",
};
// `features` is the one label a PROVIDER may override (`featuresLabel`), because
// what the tab holds differs per product; this is the neutral default.
const DETAIL_TAB_LABELS: Record<DetailTab, string> = {
  setup: "Configuration Wizard",
  automations: "Automations",
  features: "Features",
  webhook: "Webhook",
  settings: "Connection Setup",
};

/** Centers a fixed-width content column inside the full width of its host. With
 * no `width` — mobile, the connections list, or before the pane is measured — it
 * renders children unwrapped so the host's own centering stays in charge.
 *
 * This is what lets the detail view's scroll container span the whole pane,
 * putting its scrollbar on the pane edge, while the content inside it still
 * lines up with the fixed header above. */
function ContentColumn({
  width,
  children,
}: {
  width?: number | undefined;
  children: React.ReactNode;
}) {
  if (!width) return <>{children}</>;
  return (
    <Box direction="row" fill="width" justify="center" paddingX="lg" shrink={false}>
      <Box width={width}>{children}</Box>
    </Box>
  );
}

/** The scrolled body of one detail tab. The tab panel above it is the
 * full-width scroll container; this centers the panel within it. */
function TabPanelBody({
  contentWidth,
  children,
}: {
  contentWidth?: number | undefined;
  children: React.ReactNode;
}) {
  return (
    <ContentColumn width={contentWidth}>
      <Box fill="width" paddingBottom="md" paddingTop="lg">
        {children}
      </Box>
    </ContentColumn>
  );
}

/** Hover tooltip for a footer action button.
 *
 * The wrapper matters: a disabled Button sets `pointer-events: none` on itself,
 * so a tooltip bound to the button would go silent in exactly the states whose
 * copy is most useful ("Test your changes first…"). The trigger renders a plain
 * Box AROUND the button, which still receives the hover. */
function ActionTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Box direction="row" />}>{children}</TooltipTrigger>
      <TooltipPopup>
        <Text size="xs">{label}</Text>
      </TooltipPopup>
    </Tooltip>
  );
}

/** The next-step hint that sits to the left of a footer's action buttons.
 *
 * Shares the footer row with the buttons: the note shrinks (and wraps) so a long
 * sentence never pushes the buttons out of reach, and the button group holds its
 * width. Both footers render this, so the guidance chrome can't drift between
 * creating a connection and editing one. */
function FooterNote({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <Box align="center" direction="row" fill="width" gap="md" justify="between">
      <Box align="center" direction="row" gap="xs" shrink>
        <Icon color="muted" name="info" size="sm" />
        <Text size="xs" variant="muted">
          {text}
        </Text>
      </Box>
      <Box align="center" direction="row" gap="sm" shrink={false}>
        {children}
      </Box>
    </Box>
  );
}

/** A field's label with optional helper text underneath. */
function FieldLabel({ label, sublabel }: { label: string; sublabel?: string | undefined }) {
  return (
    <Box fill="width" gap="2xs">
      <Text size="sm" weight="medium">
        {label}
      </Text>
      {sublabel ? (
        <Text size="xs" variant="muted">
          {sublabel}
        </Text>
      ) : null}
    </Box>
  );
}

// Whether the browser can mask a plain text field via CSS `text-security`
// (Chrome/Safari/Edge). Lets secret fields avoid a real password input —
// and thus the password manager — while staying masked. Computed once, lazily.
let TEXT_SECURITY_SUPPORT: boolean | null = null;
function supportsTextSecurity(): boolean {
  if (TEXT_SECURITY_SUPPORT === null) {
    TEXT_SECURITY_SUPPORT =
      typeof CSS !== "undefined" &&
      typeof CSS.supports === "function" &&
      (CSS.supports("-webkit-text-security", "disc") ||
        CSS.supports("text-security", "disc"));
  }
  return TEXT_SECURITY_SUPPORT;
}

/** A labeled text field for the connect/settings forms. `secret` masks the
 * value like a password (bullets + reveal toggle) WITHOUT registering as a
 * browser credential — these are API keys/tokens, not a login. Masks via CSS
 * `-webkit-text-security` on a plain text field so the password manager never
 * offers to save/autofill it; browsers without that support (Firefox) fall
 * back to a real password field. */
function FormField({
  label,
  sublabel,
  value,
  onChangeText,
  secret,
  placeholder,
}: {
  label: string;
  sublabel?: string | undefined;
  value: string;
  onChangeText: (next: string) => void;
  secret?: boolean | undefined;
  placeholder?: string | undefined;
}) {
  const [revealed, setRevealed] = React.useState(false);
  const cssMask = !!secret && supportsTextSecurity();
  const masked = !!secret && !revealed;
  return (
    <Box fill="width" gap="2xs">
      <FieldLabel label={label} sublabel={sublabel} />
      <InputGroup>
        <InputGroupInput
          autoCapitalize="none"
          autoComplete={secret ? "new-password" : "off"}
          className={masked && cssMask ? "[-webkit-text-security:disc]" : undefined}
          placeholder={placeholder}
          type={masked && !cssMask ? "password" : "text"}
          value={value}
          onChange={(e) => onChangeText(e.target.value)}
        />
        {secret ? (
          <InputGroupAddon align="inline-end">
            <Button
              aria-label={revealed ? "Hide value" : "Show value"}
              size="icon-xs"
              variant="ghost"
              onClick={() => setRevealed((v) => !v)}
            >
              <Icon color="muted" name={revealed ? "eye-closed" : "eye"} size="sm" />
            </Button>
          </InputGroupAddon>
        ) : null}
      </InputGroup>
    </Box>
  );
}

/** A labeled multi-line field for values that carry newlines (e.g. a pasted PEM
 * key) — rendered for any auth field whose `control` is "textarea". */
function TextAreaField({
  label,
  sublabel,
  value,
  onChangeText,
  placeholder,
}: {
  label: string;
  sublabel?: string | undefined;
  value: string;
  onChangeText: (next: string) => void;
  placeholder?: string | undefined;
}) {
  return (
    <Box fill="width" gap="2xs">
      <FieldLabel label={label} sublabel={sublabel} />
      <Textarea
        autoCapitalize="none"
        autoComplete="off"
        placeholder={placeholder}
        rows={4}
        value={value}
        onChange={(e) => onChangeText(e.target.value)}
      />
    </Box>
  );
}

/**
 * New-configuration form: name the configuration + fill the provider's
 * credential fields, then Test Connection. On a passing test the button
 * becomes Save Connection; failures toast the error.
 */
/**
 * The connect form for an OAuth provider: a name, then a button that leaves for
 * the provider's consent screen.
 *
 * A separate component rather than a branch inside `NewConnectionForm` because
 * almost nothing is shared. There are no credential fields to render, and the
 * Test-then-Save gate is meaningless — there is nothing to test until the
 * provider hands back a token, and the SAVE happens server-side in the callback,
 * not on a button here.
 *
 * The name is collected UP FRONT because the redirect discards this component:
 * the callback returns with no UI context and cannot prompt for one.
 */
function NewOAuthConnectionForm({ provider }: { provider: IntegrationProvider }) {
  const [name, setName] = React.useState("");
  const { starting, handleStart } = useStartIntegrationOAuth();
  const trimmed = name.trim();

  return (
    <ConfigSection label="New connection">
      <FormField
        label="Connection name"
        placeholder={`e.g. ${provider.name} — Main`}
        value={name}
        onChangeText={setName}
      />
      <Text size="sm" variant="muted">
        {`You will be sent to ${provider.name} to authorize SalesTrak, then returned here. Nothing is saved until you approve it.`}
      </Text>
      <FooterNote
        text={
          trimmed
            ? `Authorize with ${provider.name} to finish.`
            : "Name this connection to continue."
        }
      >
        <ActionTooltip
          label={
            trimmed ? `Authorize SalesTrak in ${provider.name}` : "Enter a connection name first"
          }
        >
          <Button
            disabled={!trimmed || starting}
            loading={starting}
            onClick={() => handleStart({ provider: provider.id, name: trimmed })}
          >
            <Icon name="external-link" size="sm" />
            {`Add to ${provider.name}`}
          </Button>
        </ActionTooltip>
      </FooterNote>
    </ConfigSection>
  );
}

function NewConnectionForm({
  provider,
  onSaved,
}: {
  provider: IntegrationProvider;
  onSaved: (connectionId: string) => void;
}) {
  const {
    name,
    setNameField,
    values,
    setField,
    tested,
    testing,
    saving,
    allFilled,
    handleTest,
    handleSave,
  } = useNewConnectionForm(provider, onSaved);

  const validationError = fieldGroupError(provider.authFields, values);
  // One state object drives the note, both tooltips, and the disabled props.
  const actionState = { testing, saving, tested, allFilled, validationError };

  return (
    <ConfigSection label="New connection">
      <FormField
        label="Connection name"
        placeholder={`e.g. ${provider.name} — Main`}
        value={name}
        onChangeText={setNameField}
      />
      {provider.authFields.map((field) =>
        field.control === "list" ? (
          <ListEditor
            key={field.key}
            addLabel={field.listAddLabel}
            label={field.label}
            placeholder={field.placeholder}
            sublabel={field.sublabel}
            value={values[field.key] ?? ""}
            onChange={(next) => setField(field.key, next)}
          />
        ) : field.control === "textarea" ? (
          <TextAreaField
            key={field.key}
            label={field.label}
            placeholder={field.placeholder}
            sublabel={field.sublabel}
            value={values[field.key] ?? ""}
            onChangeText={(next) => setField(field.key, next)}
          />
        ) : (
          <FormField
            key={field.key}
            label={field.label}
            placeholder={field.placeholder}
            secret={field.secret}
            sublabel={field.sublabel}
            value={values[field.key] ?? ""}
            onChangeText={(next) => setField(field.key, next)}
          />
        ),
      )}
      {validationError ? (
        <Text size="sm" variant="destructive">
          {validationError}
        </Text>
      ) : null}

      <FooterNote text={newConnectionNextStep(actionState)}>
        {tested ? (
          <ActionTooltip label={newSaveButtonTooltip(actionState)}>
            <Button disabled={saving} loading={saving} onClick={handleSave}>
              <Icon name="check" size="sm" />
              Save Connection
            </Button>
          </ActionTooltip>
        ) : (
          <ActionTooltip label={newTestButtonTooltip(actionState)}>
            <Button
              disabled={!allFilled || testing || !!validationError}
              loading={testing}
              onClick={handleTest}
            >
              Test Connection
            </Button>
          </ActionTooltip>
        )}
      </FooterNote>
    </ConfigSection>
  );
}

/** A centered placeholder for a tab that isn't wired yet — Setup and Automations
 * both use it, and each supplies its own icon/title/copy. The tab still renders
 * so the roadmap item is visible; only its body is a stub. */
function PlaceholderPanel({
  icon,
  title,
  description,
}: {
  icon: GlyphName;
  title: string;
  description: string;
}) {
  return (
    <Box align="center" fill="width" gap="xs" justify="center" paddingY="2xl">
      <IconChip name={icon} size="lg" />
      <Text size="lg" weight="bold">
        {title}
      </Text>
      <Text size="sm" variant="muted">
        {description}
      </Text>
    </Box>
  );
}

// Workspace automations to scan for ones referencing this connection. Hoisted to
// module scope so the Apollo variables object keeps a stable identity — an inline
// object would be a new ref every render and re-subscribe forever.
const AUTOMATIONS_WHERE = { archived: false };

// How a connection is used by one automation. An automation can be BOTH — a
// webhook arrives on a connection and an action pushes back to the same one —
// so this is a set, not a single value.
type ConnectionRole = "trigger" | "action";

// Every automation that references `connectionId`, with the role(s) it plays.
// Matching is done client-side over the workspace's automations rather than with
// a dedicated query: the ids live inside each node's `config` jsonb, so filtering
// server-side would mean a jsonb scan per connection for a list that is small and
// already cached by the Manage list.
function automationsUsingConnection(
  automations: ReturnType<typeof useAutomations>["automations"],
  connectionId: string,
): { automation: (typeof automations)[number]; roles: Set<ConnectionRole> }[] {
  const matches: { automation: (typeof automations)[number]; roles: Set<ConnectionRole> }[] = [];
  for (const automation of automations) {
    const roles = new Set<ConnectionRole>();
    for (const t of automation.triggers ?? []) {
      if (inboundConnectionIdOf(t) === connectionId) roles.add("trigger");
    }
    for (const step of automation.steps ?? []) {
      if (outboundConnectionIdOf(step) === connectionId) roles.add("action");
    }
    if (roles.size) matches.push({ automation, roles });
  }
  return matches;
}

function roleLabel(roles: Set<ConnectionRole>): string {
  if (roles.has("trigger") && roles.has("action")) return "Trigger and action";
  return roles.has("trigger") ? "Trigger" : "Action";
}

/** Automations tab: every automation this connection is used by — as the trigger
 *  source, as an action target, or both — each linking into the builder. */
function AutomationsPanel({ connection }: { connection: IntegrationConnection }) {
  const { automations, initialLoading } = useAutomations(AUTOMATIONS_WHERE);
  const { openDialog } = useAutomationsDialogActions();

  const associated = useMemo(
    () => automationsUsingConnection(automations, connection.id),
    [automations, connection.id],
  );

  if (initialLoading) {
    return <ListRowsSkeleton rows={3} rowHeight={40} subtitled delayed />;
  }

  if (associated.length === 0) {
    return (
      <PlaceholderPanel
        icon="zap"
        title="Automations"
        description="Automations that use this connection will appear here."
      />
    );
  }

  return (
    <ConfigSection label="Automations">
      {associated.map(({ automation, roles }) => {
        // Live state from the run switch (`enabled`), NOT the version-lifecycle
        // `status` — toggling an automation off flips `enabled`, so `status`
        // alone would always read active. This panel is scoped to ONE
        // connection, so that connection's own `active` flag is the
        // authoritative "can't run" signal for the rows shown here — but only
        // for the ones it TRIGGERS: a disabled connection used solely as an
        // action target doesn't stop the automation from firing.
        const blockedByThisConnection = !connection.active && roles.has("trigger");
        const liveStatus = automationLiveStatus(
          automation.enabled,
          false,
          null,
          null,
          blockedByThisConnection,
        );
        return (
          <OptionRow
            key={automation.id}
            icon={toGlyphName(automation.icon?.icon) ?? "zap"}
            iconColor={(automation.icon?.color as Color) ?? "muted"}
            title={automation.name}
            // Role first — it answers "why is this listed here?", which the
            // automation's own name usually doesn't.
            description={`${roleLabel(roles)} · ${liveStatus.label}`}
            trailing={
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  openDialog({ type: "edit-automation", automationId: automation.id })
                }
              >
                <Icon name="pencil" size="sm" />
                <Text size="sm" weight="medium">
                  Manage
                </Text>
              </Button>
            }
          />
        );
      })}
    </ConfigSection>
  );
}

/** Generic multi-entry list editor (add/remove rows) for a comma/newline-joined
 * value — rendered for any auth field whose `control` is "list". */
function ListEditor({
  label,
  sublabel,
  placeholder,
  addLabel,
  value,
  onChange,
}: {
  label: string;
  sublabel?: string | undefined;
  placeholder?: string | undefined;
  addLabel?: string | undefined;
  value: string;
  onChange: (next: string) => void;
}) {
  // Each row carries a STABLE id (not its array index) so React reconciles the
  // controlled Inputs by identity — removing a middle row can't shift focus/caret
  // onto a neighbor. The emitted value stays the newline-joined buffer.
  const idRef = React.useRef(0);
  const mint = () => `row-${idRef.current++}`;
  const seed = (buffer: string): { id: string; value: string }[] => {
    const parsed = splitEntries(buffer);
    return (parsed.length ? parsed : [""]).map((value) => ({ id: mint(), value }));
  };
  const [rows, setRows] = React.useState<{ id: string; value: string }[]>(() => seed(value));

  // Re-seed when the buffer changes externally (e.g. Undo) — but not from our own
  // edits, which already produced this newline buffer. Empty rows are preserved
  // (see `commit`), so this compares against the same structure it emits.
  React.useEffect(() => {
    if (value !== rows.map((r) => r.value).join("\n")) setRows(seed(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rows is compared, not depended on; the effect re-seeds on external value only
  }, [value]);

  // Preserve row structure (including a pending blank row) in the emitted value
  // so add/remove registers as an edit; blanks are dropped at the API boundary.
  const commit = (next: { id: string; value: string }[]) => {
    setRows(next);
    onChange(next.map((r) => r.value).join("\n"));
  };

  return (
    <Box fill="width" gap="2xs">
      <FieldLabel label={label} sublabel={sublabel} />
      {rows.map((row, index) => (
        <Box key={row.id} align="center" direction="row" fill="width" gap="xs">
          <Box fill>
            <Input
              autoCapitalize="none"
              autoComplete="off"
              placeholder={placeholder}
              value={row.value}
              onChange={(e) =>
                commit(
                  rows.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)),
                )
              }
            />
          </Box>
          <Button
            aria-label="Remove entry"
            size="icon-xs"
            variant="ghost"
            onClick={() =>
              commit(
                rows.length > 1
                  ? rows.filter((_, i) => i !== index)
                  : [{ id: mint(), value: "" }],
              )
            }
          >
            <Icon color="muted" name="x" size="sm" />
          </Button>
        </Box>
      ))}
      <Box direction="row">
        <Button size="sm" variant="ghost" onClick={() => commit([...rows, { id: mint(), value: "" }])}>
          <Icon name="plus" size="sm" />
          {addLabel ?? "Add"}
        </Button>
      </Box>
    </Box>
  );
}

/** Webhook tab (ingestion providers): the inbound POST URL + copy + rotate. */
function WebhookPanel({ connection }: { connection: IntegrationConnection }) {
  const url = connection.webhookUrl ?? "";
  // No URL yet → this is a first generation, not a rotation (drives the copy).
  const { rotating, handleRotate } = useRotateWebhook(connection.id, { isFirst: !url });
  const [confirming, setConfirming] = React.useState(false);

  const handleCopy = async () => {
    if (!url) return;
    await copyText(url, "Webhook URL");
  };

  // A connection created before per-connection tokens existed has no URL yet.
  // Generating one goes through the SAME mutation as rotating: it runs the
  // provider's `registerWebhookSubscription`, which for a subscription-based CRM
  // registers the URL remotely rather than just minting a local string. Only the
  // label and surrounding copy differ.
  if (!url) {
    return (
      <ConfigSection label="Webhook URL">
        <Text size="sm" variant="muted">
          This connection doesn't have a webhook URL yet. Generate one, then point this
          provider at it — each event it sends POSTs here.
        </Text>
        <Box direction="row">
          <Button disabled={rotating} loading={rotating} size="sm" onClick={handleRotate}>
            <Icon name="reload" size="sm" />
            Generate webhook URL
          </Button>
        </Box>
      </ConfigSection>
    );
  }

  return (
    <ConfigSection label="Webhook URL">
      <Text size="sm" variant="muted">
        Point this provider at this URL — each event it sends POSTs here.
      </Text>
      <Box align="center" direction="row" fill="width" gap="xs">
        <Box fill>
          <Input readOnly value={url} />
        </Box>
        <Button size="sm" variant="outline" onClick={handleCopy}>
          <Icon name="copy" size="sm" />
          Copy
        </Button>
      </Box>

      {confirming ? (
        <Card fill="width" gap="sm" padding="md" radius="lg">
          <Text size="sm">
            Rotating generates a new URL and stops the current one immediately. Anything still
            posting to the old URL will fail until you update it.
          </Text>
          <Box direction="row" gap="sm" justify="end">
            <Button
              disabled={rotating}
              size="sm"
              variant="ghost"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={rotating}
              loading={rotating}
              size="sm"
              color="destructive"
              onClick={async () => {
                await handleRotate();
                setConfirming(false);
              }}
            >
              <Icon name="reload" size="sm" />
              Rotate URL
            </Button>
          </Box>
        </Card>
      ) : (
        <Box direction="row">
          <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
            <Icon name="reload" size="sm" />
            Rotate URL
          </Button>
        </Box>
      )}
    </ConfigSection>
  );
}

function SettingsTab({
  providerId,
  providerName,
  kind,
  oauth,
  connectionGuide,
  connectionId,
  initialName,
}: {
  providerId: string;
  providerName: string;
  kind: IntegrationKind;
  oauth: boolean;
  connectionGuide: ConnectionGuide | undefined;
  connectionId: string;
  initialName: string;
}) {
  const {
    fields,
    accountLabel,
    connectionGuide: resolvedGuide,
    credentialsUnreadable,
    initialLoading,
    name,
    setNameField,
    values,
    setField,
    tested,
    testing,
    saving,
    isDirty,
    handleTest,
    handleSave,
    handleReset,
  } = useConnectionSettings({ providerId, connectionId, initialName });

  if (initialLoading) {
    // The form's silhouette: two labeled fields and the footer's action pair.
    return (
      <SkeletonGroup className="flex flex-col gap-4" aria-hidden>
        {[0, 1].map((index) => (
          <Box key={index} direction="column" gap="xs">
            <Skeleton height={12} width={index === 0 ? 64 : 112} />
            <Skeleton fillWidth height={32} className="rounded-md" />
          </Box>
        ))}
        <Box direction="row" justify="end" gap="sm" paddingTop="sm">
          <Skeleton height={28} width={64} className="rounded-md" />
          <Skeleton height={28} width={56} className="rounded-md" />
        </Box>
      </SkeletonGroup>
    );
  }

  // An OAuth connection has no editable credentials — the token came from the
  // redirect and is replaced by running it again. So this tab keeps only the
  // rename (with its own Save, since Test has nothing to gate) and offers
  // Reconnect. The rename still goes through `updateConnectionCredentials`: with
  // no auth fields, `mergeCredentials` iterates an empty list and leaves the
  // stored token untouched.
  if (oauth) {
    return (
      <OAuthSettingsTab
        accountLabel={accountLabel}
        // The RESOLVED guide wins: it is the only one that can name this
        // install's bot handle. The server already fell back to the provider's
        // static guide when it could not resolve one.
        connectionGuide={resolvedGuide ?? connectionGuide}
        connectionId={connectionId}
        initialName={initialName}
        isDirty={isDirty}
        name={name}
        providerId={providerId}
        providerName={providerName}
        saving={saving}
        setNameField={setNameField}
        onSave={handleSave}
      />
    );
  }

  const isIngestion = kind === "ingestion";
  // What this provider's auth fields ARE: an ingestion provider collects
  // validation settings, everything else collects credentials. Matches the
  // section heading below, so the warning names them the same way the form does.
  const settingsNoun = isIngestion ? "settings" : "credentials";
  const validationError = fieldGroupError(fields, values);
  // One state object feeds both the `disabled` props and the tooltip copy, so a
  // tooltip can never name a reason the button doesn't actually have.
  const actionState = {
    testing,
    saving,
    tested,
    isDirty,
    nameEmpty: name.trim().length === 0,
    validationError,
  };
  const testDisabled =
    actionState.testing ||
    actionState.saving ||
    actionState.nameEmpty ||
    !!actionState.validationError;
  const saveDisabled = !actionState.tested || actionState.testing || actionState.saving;

  return (
    <Box fill="width" gap="xl">
      {/* The stored blob didn't decrypt, so the fields below are blank whatever
       * is saved. Says so up front — the alternative (what this replaced) was a
       * form that looked merely unconfigured, on a connection that silently
       * rejects every event. Saving the form overwrites the unreadable blob, so
       * this tab is also the fix. */}
      {credentialsUnreadable ? (
        <Alert variant="warning">
          <Icon name="alert-triangle" />
          <AlertTitle>
            This connection&rsquo;s saved {settingsNoun} could not be read
          </AlertTitle>
          <AlertDescription>
            <Text size="sm">
              They were encrypted with a different key than this environment
              uses — which is what a connection copied between environments looks
              like. Until you re-enter the {settingsNoun} below and save, this
              connection rejects inbound events and fails outbound actions.
            </Text>
          </AlertDescription>
        </Alert>
      ) : null}

      <ConfigSection label="Connection">
        <FormField
          label="Connection name"
          placeholder="Connection name"
          value={name}
          onChangeText={setNameField}
        />
      </ConfigSection>

      <ConfigSection label={isIngestion ? "Settings" : "Authentication"}>
        {fields.map((field) =>
          field.control === "list" ? (
            <ListEditor
              key={field.key}
              addLabel={field.listAddLabel ?? undefined}
              label={field.label}
              placeholder={field.placeholder ?? undefined}
              sublabel={field.sublabel ?? undefined}
              value={values[field.key] ?? ""}
              onChange={(next) => setField(field.key, next)}
            />
          ) : field.control === "textarea" ? (
            <TextAreaField
              key={field.key}
              label={field.label}
              placeholder={field.placeholder ?? undefined}
              sublabel={field.sublabel ?? undefined}
              value={values[field.key] ?? ""}
              onChangeText={(next) => setField(field.key, next)}
            />
          ) : (
            <FormField
              key={field.key}
              label={field.label}
              placeholder={
                field.secret && field.hint
                  ? `Current ${field.hint} — leave blank to keep`
                  : field.placeholder ?? undefined
              }
              secret={field.secret}
              sublabel={field.sublabel ?? undefined}
              value={values[field.key] ?? ""}
              onChangeText={(next) => setField(field.key, next)}
            />
          ),
        )}
        {validationError ? (
          <Text size="sm" variant="destructive">
            {validationError}
          </Text>
        ) : null}
      </ConfigSection>

      <ConnectionGuideSection guide={resolvedGuide ?? connectionGuide} />

      {/* Undo unsaved edits (only while dirty), then Test and Save side by side —
       * both always present, so the tab never hides the action you're looking
       * for. Save is gated on `tested` rather than swapped in: the same rule as
       * before, since `useConnectionSettings` clears `tested` on every edit (and
       * after a save), so untested changes can't be saved. Test stays secondary
       * and Save primary — Save is the goal, Test the step on the way. */}
      <FooterNote text={settingsNextStep(actionState)}>
        {isDirty ? (
          <Button disabled={testing || saving} variant="ghost" onClick={handleReset}>
            <Icon name="undo" size="sm" />
            Undo changes
          </Button>
        ) : null}
        <ActionTooltip label={testButtonTooltip(actionState)}>
          <Button disabled={testDisabled} loading={testing} variant="outline" onClick={handleTest}>
            Test Connection
          </Button>
        </ActionTooltip>
        <ActionTooltip label={saveButtonTooltip(actionState)}>
          <Button disabled={saveDisabled} loading={saving} onClick={handleSave}>
            <Icon name="check" size="sm" />
            Save Changes
          </Button>
        </ActionTooltip>
      </FooterNote>
    </Box>
  );
}

/**
 * One browsable collection on the Features tab: a search box, a scrollable list,
 * and a re-sync.
 *
 * Generic over providers — the heading, the rows and the source key all come
 * from the server, so nothing here knows what a given collection holds.
 *
 * The list is capped in height and scrolls INSIDE the panel rather than growing
 * the tab. A large account would otherwise push the re-sync button and every
 * other section off the bottom of the page.
 */
function RemoteCollectionSection({
  connectionId,
  collection,
}: {
  connectionId: string;
  collection: RemoteCollection;
}) {
  const [search, setSearch] = React.useState("");
  // Debounced: every keystroke is a server round trip otherwise, because the
  // provider's list is filtered server-side against a cached scan.
  const debouncedSearch = useDebouncedValue(search, 250);
  const {
    options,
    truncated,
    error,
    initialLoading,
    refreshing,
    lastSyncedAt,
    totalCount,
    ceilingHit,
    handleRefresh,
  } = useRemoteCollection(connectionId, collection.key, debouncedSearch);

  return (
    <ConfigSection label={collection.label}>
      {collection.description ? (
        <Text size="sm" variant="muted">
          {collection.description}
        </Text>
      ) : null}

      {/* What this list IS right now, in one line. Without it an admin cannot
          tell a small account from a stale one, and the Re-sync button beside it
          is a guess rather than a decision. */}
      <Text size="xs" variant="muted">
        {refreshing
          ? `Reading from the connected account… ${totalCount.toLocaleString()} so far`
          : lastSyncedAt
            ? `${totalCount.toLocaleString()} synced · last checked ${formatRelativeDate(lastSyncedAt)}`
            : "Not synced yet."}
      </Text>

      <Box align="center" direction="row" fill="width" gap="xs">
        <Box fill>
          <Input
            value={search}
            placeholder={`Search ${collection.label.toLowerCase()}`}
            autoCapitalize="none"
            onChange={(event) => setSearch(event.target.value)}
          />
        </Box>
        <Button
          disabled={refreshing}
          loading={refreshing}
          size="sm"
          variant="outline"
          onClick={handleRefresh}
        >
          <Icon name="reload" size="sm" />
          Re-sync
        </Button>
      </Box>

      {error ? (
        <Text size="sm" variant="destructive">
          {error}
        </Text>
      ) : ceilingHit ? (
        // Expected never to render: the ceiling is a backstop against a provider
        // that paginates wrongly, not a budget. It is surfaced because a silently
        // capped list is the exact failure the synced directory removed.
        <Text size="sm" variant="destructive">
          This list stopped at its size limit, so some {collection.label.toLowerCase()} are
          missing.
        </Text>
      ) : null}

      {initialLoading ? (
        <ListRowsSkeleton rows={5} rowHeight={32} />
      ) : options.length === 0 ? (
        <Text size="sm" variant="muted">
          {search.trim()
            ? `No ${collection.label.toLowerCase()} match "${search.trim()}".`
            : `No ${collection.label.toLowerCase()} to show.`}
        </Text>
      ) : (
        // Capped and scrolled INSIDE the panel: an account with a thousand
        // channels would otherwise push the re-sync button and every later
        // section off the bottom of the page.
        <Box direction="column" maxHeight={360}>
          <ScrollArea>
            <Box fill="width" gap="2xs">
            {options.map((option) => (
              <Box
                key={option.value}
                align="center"
                direction="row"
                gap="sm"
                paddingX="sm"
                paddingY="xs"
              >
                {option.icon ? (
                  <Icon color="muted" name={option.icon as IconName} size="sm" />
                ) : null}
                <Box fill gap="2xs" shrink>
                  <Text size="sm" truncate={1}>
                    {option.label}
                  </Text>
                  {option.description ? (
                    <Text size="xs" truncate={1} variant="muted">
                      {option.description}
                    </Text>
                  ) : null}
                </Box>
                </Box>
              ))}
            </Box>
          </ScrollArea>
        </Box>
      )}

      {/* After the list, not before: it describes what was just rendered. An
          error already says the list is incomplete, so do not say it twice. */}
      {truncated && !error ? (
        <Text size="xs" variant="muted">
          Showing part of the list. Search covers only these.
        </Text>
      ) : null}
    </ConfigSection>
  );
}

/**
 * The Features tab: whatever this provider exposes from the connected account.
 *
 * Rendered only when the provider declares at least one collection, so this
 * never shows an empty state — `tabsForProvider` hides the tab entirely.
 */
function FeaturesPanel({
  connectionId,
  collections,
}: {
  connectionId: string;
  collections: readonly RemoteCollection[];
}) {
  return (
    <Box fill="width" gap="xl">
      {collections.map((collection) => (
        <RemoteCollectionSection
          key={collection.key}
          collection={collection}
          connectionId={connectionId}
        />
      ))}
    </Box>
  );
}

/**
 * A provider-declared manual step, shown on a connection's Setup tab — the thing
 * the connection cannot do for the admin because it happens inside the
 * provider's own product, because no OAuth scope can grant it.
 *
 * Generic over providers: the title, prose and command all come from the server,
 * so nothing here knows which provider it is rendering. Renders nothing when the
 * provider declares no guide.
 *
 * The command sits in the same read-only input + Copy pairing as the webhook
 * URL. It is a literal someone retypes into another product, and retyping a
 * slash command wrong is the failure this section exists to prevent.
 */
function ConnectionGuideSection({ guide }: { guide: ConnectionGuide | undefined }) {
  if (!guide) return null;
  // Bound to a local so the click handler closes over a narrowed `string` — a
  // property read inside the closure would widen back to `string | undefined`.
  const command = guide.command;
  return (
    <ConfigSection label={guide.title}>
      <Text size="sm" variant="muted">
        {guide.note}
      </Text>
      {command ? (
        <Box align="center" direction="row" fill="width" gap="xs">
          <Box fill>
            <Input readOnly value={command} />
          </Box>
          <Button size="sm" variant="outline" onClick={() => copyText(command, "Command")}>
            <Icon name="copy" size="sm" />
            Copy
          </Button>
        </Box>
      ) : null}
    </ConfigSection>
  );
}

/**
 * The Connection Setup tab for an OAuth connection: rename, and re-authorize.
 *
 * Save is gated on `isDirty` alone, NOT on the `tested` flag the credential form
 * uses. There is no Test button here — testing means "are these typed
 * credentials valid", and the only credential is a token this UI never sees. The
 * server still re-tests on save, so a rename against a revoked install fails
 * with the provider's own message.
 *
 * `accountLabel` names the external account the connection installed into. It is
 * read-only and derived from stored credentials by the
 * provider, so it is not one of `fields` and never joins the dirty check. Null
 * for a provider that names no account — render nothing rather than a row that
 * says "Unknown".
 */
function OAuthSettingsTab({
  providerId,
  providerName,
  connectionId,
  initialName,
  name,
  setNameField,
  accountLabel,
  connectionGuide,
  isDirty,
  saving,
  onSave,
}: {
  providerId: string;
  providerName: string;
  connectionId: string;
  initialName: string;
  name: string;
  setNameField: (next: string) => void;
  accountLabel: string | null;
  connectionGuide: ConnectionGuide | undefined;
  isDirty: boolean;
  saving: boolean;
  onSave: () => void;
}) {
  const { starting, handleStart } = useStartIntegrationOAuth();
  const nameEmpty = name.trim().length === 0;

  return (
    <Box fill="width" gap="xl">
      <ConfigSection label="Connection">
        <FormField
          label="Connection name"
          placeholder="Connection name"
          value={name}
          onChangeText={setNameField}
        />
      </ConfigSection>

      <ConfigSection label="Authorization">
        {accountLabel ? (
          <Box direction="row" gap="sm">
            <Text size="sm" variant="muted">
              {`Connected ${providerName} workspace:`}
            </Text>
            <Text size="sm" weight="medium">
              {accountLabel}
            </Text>
          </Box>
        ) : null}
        <Text size="sm" variant="muted">
          {`Reconnect to replace this connection's access — for example after someone removed the app in ${providerName}, or to install it into a different workspace.`}
        </Text>
        <FooterNote text={`Reconnecting replaces the stored ${providerName} access.`}>
          <ActionTooltip label={`Authorize SalesTrak in ${providerName} again`}>
            <Button
              disabled={starting}
              loading={starting}
              variant="outline"
              onClick={() =>
                // Reconnect targets THIS connection, so the callback replaces its
                // token instead of creating a second one. The name sent is the
                // SAVED name, not the edited field — an unsaved rename is not
                // part of re-authorizing, and the redirect would discard it.
                handleStart({ provider: providerId, name: initialName, connectionId })
              }
            >
              <Icon name="external-link" size="sm" />
              {`Reconnect to ${providerName}`}
            </Button>
          </ActionTooltip>
        </FooterNote>
      </ConfigSection>

      <ConnectionGuideSection guide={connectionGuide} />

      <FooterNote
        text={
          nameEmpty
            ? "A connection needs a name."
            : isDirty
              ? "Save to apply the new name."
              : "No unsaved changes."
        }
      >
        <ActionTooltip
          label={
            nameEmpty
              ? "Enter a connection name"
              : isDirty
                ? "Save the new name"
                : "Nothing to save"
          }
        >
          <Button disabled={!isDirty || nameEmpty || saving} loading={saving} onClick={onSave}>
            <Icon name="check" size="sm" />
            Save Changes
          </Button>
        </ActionTooltip>
      </FooterNote>
    </Box>
  );
}

/** One detail tab's panel: full-width scroll container in bounded (fillHeight)
 * mode — its scrollbar lands on the pane edge — and a plain block otherwise,
 * where the host page scrolls. */
function DetailTabPanel({
  value,
  bounded,
  contentWidth,
  children,
}: {
  value: DetailTab;
  bounded: boolean;
  contentWidth?: number | undefined;
  children: React.ReactNode;
}) {
  return (
    <TabsPanel value={value}>
      {bounded ? (
        <ScrollArea scrollbarGutter>
          <TabPanelBody contentWidth={contentWidth}>{children}</TabPanelBody>
        </ScrollArea>
      ) : (
        <TabPanelBody contentWidth={contentWidth}>{children}</TabPanelBody>
      )}
    </TabsPanel>
  );
}

/** Detail view for a selected, persisted connection: status toggle + delete
 * above type-driven tabs. Navigating back to the connections list is the host
 * breadcrumb's job (`ConfigCrumb`), not this view's — `onBack` only fires after
 * a delete, when the connection this view renders no longer exists. */
function ConnectionDetailView({
  provider,
  connection,
  onBack,
  fillHeight,
  contentWidth,
}: {
  provider: IntegrationProvider;
  connection: IntegrationConnection;
  onBack: () => void;
  fillHeight?: boolean | undefined;
  contentWidth?: number | undefined;
}) {
  const { active, statusSaving, handleStatus } = useConnectionStatus(connection);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);

  // The Features tab's query is gated on `automations.manage`, which is NOT
  // implied by the `workspace.integrations` needed to reach this panel.
  const canReadRemoteOptions = usePermission("automations.manage");
  const tabs = tabsForProvider(provider, { canReadRemoteOptions });
  // Open on whatever comes FIRST rather than a hardcoded tab. `tabsForProvider`
  // drops Setup for a provider that declares no wizard fields, and naming a tab
  // that is not in the list selects nothing — the strip renders with no panel
  // under it. Reading the list keeps the default correct as the list changes.
  const [tab, setTab] = React.useState<DetailTab>(tabs[0] ?? "settings");

  return (
    <Box
      fill={fillHeight ? true : "width"}
      gap="lg"
      // A flex child defaults to `min-height: auto`, which would let this column
      // grow past the pane instead of handing the overflow to the tab panel.
      minHeight={fillHeight ? 0 : undefined}
    >
      {/* Connection name, with the status toggle + delete on the same baseline.
       * The name shrinks (and truncates) so a long one never pushes the
       * controls off the row. */}
      <ContentColumn width={contentWidth}>
        <Box align="center" direction="row" gap="md" justify="between" shrink={false}>
          <Box shrink>
            <Text size="lg" truncate={1} weight="bold">
              {connection.name}
            </Text>
          </Box>
          <Box align="center" direction="row" gap="md" shrink={false}>
            <Box align="center" direction="row" gap="xs">
              {/* The switch still reflects `active`, because that IS what it
                  toggles — but the label reports HEALTH, which the switch cannot
                  change. Showing "Active" beside a connection the provider has
                  revoked is the exact confusion this state was added to end. */}
              <Text
                size="sm"
                variant={connection.needsAttention ? "warning" : active ? "success" : "muted"}
                weight="medium"
              >
                {connection.needsAttention ? "Needs attention" : active ? "Active" : "Disabled"}
              </Text>
              {/* INERT while the connection needs attention. A live switch sitting
                  beside the words "Needs attention" reads as the control for that
                  state, and it is not — it would flip `active`, which changes
                  nothing about a connection that already cannot run, and leaves
                  the warning exactly where it was. The tooltip names the control
                  that DOES fix it. Same treatment as the Action node's power
                  button, for the same reason.

                  ActionTooltip wraps rather than binds: a disabled control sets
                  `pointer-events: none` on itself, so a tooltip attached to the
                  Switch would go silent in precisely the state whose copy
                  matters. */}
              <ActionTooltip
                label={
                  connection.needsAttention
                    ? "This connection can't run until it is reconnected, so turning it off changes nothing. Reconnect it on the Connection Setup tab."
                    : active
                      ? "Turn this connection off. Automations that use it stop running."
                      : "Turn this connection back on."
                }
              >
                <Switch
                  checked={active}
                  disabled={statusSaving || connection.needsAttention}
                  onCheckedChange={handleStatus}
                />
              </ActionTooltip>
            </Box>
            <Button
              aria-label="Delete connection"
              disabled={confirmingDelete}
              size="icon-xs"
              variant="ghost"
              onClick={() => setConfirmingDelete(true)}
            >
              <Icon color="destructive" name="trash" />
            </Button>
          </Box>
        </Box>
      </ContentColumn>

      {/* Above the tabs, so it is seen whichever one is open. The provider's own
          sentence names both what happened and the single act that fixes it —
          the status word alone says something is wrong without saying what, and
          this is a state nobody in the workspace caused or can otherwise
          discover. */}
      {connection.needsAttention ? (
        <ContentColumn width={contentWidth}>
          <Alert variant="warning">
            <AlertTitle>This connection needs attention</AlertTitle>
            <AlertDescription>
              {connection.attentionMessage ??
                "It cannot run until it is reconnected."}
            </AlertDescription>
          </Alert>
        </ContentColumn>
      ) : null}

      {confirmingDelete ? (
        <ContentColumn width={contentWidth}>
          <DeleteConnectionConfirm
            connection={{ id: connection.id, name: connection.name }}
            providerId={provider.id}
            onCancel={() => setConfirmingDelete(false)}
            onDeleted={onBack}
          />
        </ContentColumn>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as DetailTab)}>
          <ContentColumn width={contentWidth}>
            <TabsList variant="underline">
              {tabs.map((key) => (
                <TabsTab key={key} value={key}>
                  <Icon name={DETAIL_TAB_ICONS[key]} size="sm" />
                  {key === "features"
                    ? (provider.featuresLabel ?? DETAIL_TAB_LABELS[key])
                    : DETAIL_TAB_LABELS[key]}
                </TabsTab>
              ))}
            </TabsList>
          </ContentColumn>

          {/* The Setup Wizard is a roadmap item, so this panel is still a
            * placeholder — the client-side rendering for setup fields was
            * removed until the wizard lands, though the server keeps the whole
            * contract (`Provider.setup` + `config.setup` persistence + its
            * query/mutation), so only this view has to come back.
            *
            * Guarded like the Webhook panel below: `tabsForProvider` only offers
            * the tab when the provider declares setup fields, and the two must
            * agree or a tab renders with nothing behind it. No provider declares
            * any today, so this is currently unreachable — which is the point,
            * since a provider with nothing to map should not show a tab that can
            * only say "Coming soon". */}
          {tabs.includes("setup") ? (
            <DetailTabPanel bounded={!!fillHeight} contentWidth={contentWidth} value="setup">
              <PlaceholderPanel
                // "data", not "records" — the repo reserves that word (entities).
                description="Map this CRM's data to SalesTrak. Coming soon."
                icon="wrench"
                title="Setup wizard"
              />
            </DetailTabPanel>
          ) : null}

          {/* The automations that use this connection, filtered to it — this is
            * the panel dev's tree still had as a "coming soon" placeholder. */}
          <DetailTabPanel bounded={!!fillHeight} contentWidth={contentWidth} value="automations">
            <AutomationsPanel connection={connection} />
          </DetailTabPanel>

          {/* Guarded like the other conditional panels: `tabsForProvider` only
            * offers this tab when the provider declares a collection, and the
            * two must agree or a tab renders with nothing behind it. */}
          {tabs.includes("features") ? (
            <DetailTabPanel bounded={!!fillHeight} contentWidth={contentWidth} value="features">
              <FeaturesPanel
                collections={provider.remoteCollections}
                connectionId={connection.id}
              />
            </DetailTabPanel>
          ) : null}

          {/* Guarded on the SAME condition as the tab strip (`tabsForProvider`) —
           * they have to agree, or the tab renders with no panel behind it. */}
          {tabs.includes("webhook") ? (
            <DetailTabPanel bounded={!!fillHeight} contentWidth={contentWidth} value="webhook">
              <WebhookPanel connection={connection} />
            </DetailTabPanel>
          ) : null}

          <DetailTabPanel bounded={!!fillHeight} contentWidth={contentWidth} value="settings">
            <SettingsTab
              connectionGuide={provider.connectionGuide}
              connectionId={connection.id}
              initialName={connection.name}
              kind={provider.kind}
              oauth={provider.oauth}
              providerId={provider.id}
              providerName={provider.name}
            />
          </DetailTabPanel>
        </Tabs>
      )}
    </Box>
  );
}

export function ConnectionConfigView({
  provider,
  activeConnectionId,
  onActiveConnectionIdChange: setActiveConnectionId,
  fillHeight,
  contentWidth,
}: {
  provider: IntegrationProvider;
  /** Master → detail: the tabs/form appear once a connection is selected (or a
   * new one is added); `null` = the list of connections. Owned by the host so
   * its breadcrumb can render and navigate this level. */
  activeConnectionId: string | null;
  onActiveConnectionIdChange: (next: string | null) => void;
  /** The host has bounded this panel's height (the desktop pane) rather than
   * wrapping it in a scroll container, so the provider header, connection name
   * row and tab strip stay put and only the active tab's content scrolls. Omitted
   * on mobile, where the whole drawer scrolls as one. */
  fillHeight?: boolean | undefined;
  /** Pixel width of the centered content column. Set only alongside
   * `fillHeight`: the panel then centers each of its own regions, leaving the
   * scroll container full-width so its scrollbar sits on the pane edge. Unset,
   * the host does the centering and every region just fills the width it's
   * given. */
  contentWidth?: number | undefined;
}) {
  const activeConnection =
    activeConnectionId && activeConnectionId !== NEW_CONNECTION
      ? provider.connections.find((c) => c.id === activeConnectionId) ?? null
      : null;

  // If the selected connection disappears (deleted → catalog refetch), return
  // to the list.
  React.useEffect(() => {
    if (
      activeConnectionId &&
      activeConnectionId !== NEW_CONNECTION &&
      !provider.connections.some((c) => c.id === activeConnectionId)
    ) {
      setActiveConnectionId(null);
    }
  }, [activeConnectionId, provider.connections, setActiveConnectionId]);

  return (
    <Box
      fill={fillHeight ? true : "width"}
      gap="xl"
      minHeight={fillHeight ? 0 : undefined}
    >
      {/* Provider header */}
      <ContentColumn width={contentWidth}>
        <Box align="center" direction="row" gap="md" shrink={false}>
          <IconChip color={provider.color} logo={provider.logo} name={provider.icon} size="lg" />
          <Box fill gap="2xs">
            <Text size="lg" weight="bold">
              {provider.name}
            </Text>
            <Text size="sm" variant="muted">
              {provider.description}
            </Text>
          </Box>
        </Box>
      </ContentColumn>

      {activeConnectionId === null ? (
        <ConfigSection label="Connections">
          {provider.connections.map((connection, index) => (
            <OptionRow
              key={`${connection.id}-${index}`}
              // Needs-attention outranks the switch: a connection can be ON and
              // still broken, and "Active" would be a plain lie about one whose
              // app was uninstalled.
              description={`Status: ${
                connection.needsAttention
                  ? "Needs attention"
                  : connection.active
                    ? "Active"
                    : "Disabled"
              }`}
              icon={provider.icon}
              iconColor={provider.color}
              logo={provider.logo}
              title={connection.name}
              // The whole row opens the connection — no separate Configure
              // button, matching the Add-connection row below and the
              // marketplace cards.
              onClick={() => setActiveConnectionId(connection.id)}
            />
          ))}
          <OptionRow
            description={
              provider.connections.length === 0
                ? "No connections yet — connect this provider."
                : "Set up another connection."
            }
            icon="plus"
            title="Add connection"
            onClick={() => setActiveConnectionId(NEW_CONNECTION)}
          />
        </ConfigSection>
      ) : activeConnectionId === NEW_CONNECTION ? (
        // An OAuth provider has no credential form and no local save — the
        // callback creates the connection server-side, so there is no `onSaved`
        // to drill into the new connection with.
        provider.oauth ? (
          <NewOAuthConnectionForm provider={provider} />
        ) : (
          <NewConnectionForm
            provider={provider}
            onSaved={(connectionId) => setActiveConnectionId(connectionId)}
          />
        )
      ) : activeConnection ? (
        <ConnectionDetailView
          key={activeConnection.id}
          connection={activeConnection}
          contentWidth={contentWidth}
          fillHeight={fillHeight}
          provider={provider}
          onBack={() => setActiveConnectionId(null)}
        />
      ) : null}
    </Box>
  );
}
